// Demo Plutoo - Client Configuration (Testing Environment)
import { FEATURES } from '../features.js';

export const DEMO_PLUTOO_CONFIG = {
  // Client identification
  clientName: 'demo-plutoo',
  displayName: 'Plutoo Demo Environment',
  
  // Branding configuration
  branding: {
    companyName: 'Plutoo Property Management Demo',
    shortName: 'Plutoo Demo',
    logo: '/uploads/logos/plutoo-demo-logo.png',
    logoAlt: 'Plutoo Demo Logo',
    favicon: '/favicon.jpg',
    
    // Theme colors - vibrant for demo
    primaryColor: '#1976D2',      // Material Blue
    secondaryColor: '#42A5F5',    // Light blue
    accentColor: '#90CAF9',       // Very light blue
    backgroundColor: '#E3F2FD',   // Subtle blue background
    textColor: '#0D47A1',         // Dark blue text
    
    // Theme name for CSS classes
    theme: 'demo-theme',
    
    // Custom CSS variables
    customCss: {
      '--primary-color': '#1976D2',
      '--secondary-color': '#42A5F5',
      '--accent-color': '#90CAF9',
      '--background-color': '#E3F2FD',
      '--text-color': '#0D47A1',
    }
  },
  
  // Firebase configuration
  firebase: {
    projectId: process.env.REACT_APP_DEMO_FIREBASE_PROJECT_ID,
    databaseURL: process.env.REACT_APP_DEMO_FIREBASE_DATABASE_URL,
    apiKey: process.env.REACT_APP_DEMO_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_DEMO_FIREBASE_AUTH_DOMAIN,
    storageBucket: process.env.REACT_APP_DEMO_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_DEMO_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_DEMO_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_DEMO_FIREBASE_MEASUREMENT_ID
    // Note: All Firebase configuration must be provided via environment variables
    // See ENVIRONMENT_VARIABLES_GUIDE.md for required variables
  },
  
  // Domain and deployment configuration
  deployment: {
    domain: 'demoprop.plutoo.co.uk',
    ftpPath: '/public_html/demoprop',
    buildPath: './build-demo-plutoo/',
  },
  
  // Client-specific feature overrides
  // Demo environment gets access to ALL new features for testing
  features: {
    // Enable ALL new features for demo testing
    [FEATURES.AUTOMATED_ALERTS]: true,
    
    // Future features can be enabled here first
    [FEATURES.TENANT_PORTAL]: true,  // Demo can test this
  },
  
  // Client-specific customizations
  customizations: {
    // UI/UX customizations - show off all features
    showAdvancedFilters: true,
    maxPropertiesPerPage: 100,
    enablePropertyGroups: true,
    showPropertyImages: true,
    defaultSortOrder: 'created_date',
    
    // Dashboard customizations - show all widgets
    dashboardWidgets: [
      'property_overview',
      'recent_maintenance',
      'rent_summary',
      'upcoming_tasks',
      'property_analytics',
      'compliance_alerts',
      'tenant_portal_activity'
    ],
    
    // Navigation customizations
    sidebarCollapsed: false,
    showQuickActions: true,
    showBetaFeatures: true,    // Demo shows beta features
    
    // Financial settings
    currencySymbol: '£',
    currencyCode: 'GBP',
    dateFormat: 'DD/MM/YYYY',
    
    // Property management specific
    allowMultipleLeases: true,
    requirePropertyPhotos: false,  // Demo doesn't require photos
    autoGeneratePropertyCodes: true,
    propertyCodePrefix: 'DEMO',
    
    // Tenant management
    requireTenantReferences: false,  // Demo doesn't require references
    allowJointTenancies: true,
    autoSendWelcomeEmails: true,    // Demo shows email features
    
    // Maintenance settings
    allowTenantMaintenanceRequests: true,
    requireMaintenancePhotos: false,
    autoAssignMaintenanceToPreferredContractors: false,
    
    // Notification preferences - all enabled for demo
    emailNotifications: {
      newTenant: true,
      maintenanceRequest: true,
      rentOverdue: true,
      certificateExpiry: true,
      newFeatureAnnouncements: true,
    },
    
    // Reporting preferences
    defaultReportPeriod: 'monthly',
    includeVATInReports: true,
    showProfitLossReports: true,
    showAdvancedAnalytics: true,
    
    // Demo-specific settings
    showDemoData: true,
    enableDemoMode: true,
    showFeatureTooltips: true,
    allowDataReset: true,
  },
  
  // Client-specific menu structure - includes all features
  menuStructure: {
    dashboard: { enabled: true, order: 1 },
    properties: { enabled: true, order: 2 },
    tenants: { enabled: true, order: 3 },
    maintenance: { enabled: true, order: 4 },
    finance: { enabled: true, order: 5 },
    reports: { enabled: true, order: 6 },
    analytics: { enabled: true, order: 7 },  // Demo gets analytics
    messages: { enabled: true, order: 8 },
    settings: { enabled: true, order: 9 },
    help: { enabled: true, order: 10 },      // Demo gets help section
  },
  
  // Client contact information
  support: {
    email: 'demo@plutoo.co.uk',
    phone: '+44 (0) 800 DEMO (3366)',
    hours: '24/7 Demo Support',
  },
  
  // Terms and compliance
  legal: {
    termsOfService: '/terms-demo',
    privacyPolicy: '/privacy-demo',
    dataRetentionPeriod: '30 days',  // Demo data cleaned regularly
    gdprCompliant: true,
  },
  
  // Demo-specific configuration
  demo: {
    resetDataDaily: true,
    seedWithSampleData: true,
    showOnboarding: true,
    enableFeatureTour: true,
    allowScreenshots: true,
    watermarkImages: true,
  }
}; 